import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ApiError, Client } from '../../lib/api';
import Modal from '../ui/Modal';
import { useToast } from '../ui/Toast';

const createClientSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().max(500).optional(),
});

type CreateClientFormData = z.infer<typeof createClientSchema>;

export interface CreateClientModalProps {
  entityId: string;
  onClose: () => void;
  onSuccess: (client?: Client) => void;
  isEmployee?: boolean;
  title?: string;
  initialName?: string;
}

export function CreateClientModal({
  entityId,
  onClose,
  onSuccess,
  isEmployee = false,
  title,
  initialName,
}: CreateClientModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateClientFormData>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      name: initialName || '',
      description: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateClientFormData) =>
      api.clients.create(entityId, {
        name: data.name,
        description: data.description || undefined,
        ...(isEmployee ? { isEmployee: true } : {}),
      }),
    onSuccess: (res) => {
      toast.success(
        isEmployee
          ? t('cashier.clients.createEmployeeSuccess', 'Employee added')
          : t('cashier.clients.createSuccess', 'Client created successfully')
      );
      onSuccess(res.data);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const defaultTitle = isEmployee
    ? t('cashier.clients.newEmployee', 'New Employee')
    : t('cashier.clients.newClient', 'New Client');

  return (
    <Modal isOpen onClose={onClose} title={title || defaultTitle} size="sm">
      <form onSubmit={handleSubmit((data) => createMutation.mutate(data))}>
        <div className="form-group">
          <label className="form-label">
            {isEmployee
              ? t('cashier.clients.fields.employeeName', 'Employee name')
              : t('cashier.clients.fields.name', 'Name')}
          </label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            placeholder={
              isEmployee
                ? t('cashier.clients.fields.employeeNamePlaceholder', 'Enter employee name...')
                : t('cashier.clients.fields.namePlaceholder', 'Enter client name...')
            }
            {...register('name')}
            autoFocus
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('cashier.clients.fields.description', 'Description')}</label>
          <textarea
            className="form-input"
            rows={3}
            placeholder={t('cashier.clients.fields.descriptionPlaceholder', 'Optional notes about this client...')}
            {...register('description')}
          />
          {errors.description && (
            <span className="block text-error text-[13px] mt-1">{errors.description.message}</span>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? t('common.loading', 'Loading...') : t('common.create', 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
